import React, { useState, useEffect, useCallback } from "react";
import { X, Trash2, Search } from "lucide-react";
import { InverntroyService } from "../../../services/api/inventroy.service";
import toast from "react-hot-toast";
import { useAuth } from "../../../context/AuthContext";

interface CreateRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface RequestProduct {
  productId: string | null;
  productName: string;
  quantity: number | "";
}

const CreateRequestModal: React.FC<CreateRequestModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<RequestProduct[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const loadProductOptions = useCallback(
    async (page: number = 1, searchText: string = "") => {
      try {
        setLoading(true);

        const response = await InverntroyService.getProductListing({
          page,
          pageSize: 10,
          searchText,
        });

        if (!response || !Array.isArray(response.data)) {
          throw new Error("Invalid product data structure received");
        }

        const productList = response.data.map((product: any) => ({
          id: product.id,
          name: product.name || "Unnamed Product",
        }));

        setSearchResults((prev) =>
          page === 1 ? productList : [...prev, ...productList],
        );

        setLoading(false);
      } catch (error) {
        console.error("Error loading products:", error);
        toast.error("Failed to load product options");
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm) {
        setShowDropdown(true);
        loadProductOptions(1, searchTerm);
      } else {
        setSearchResults([]);
        setShowDropdown(false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, loadProductOptions]);

  const handleAddProduct = (product: any) => {
    const exists = selectedProducts.find(
      (p) => p.productId === product.id || p.productName === product.name,
    );
    if (exists) {
      toast.error("Product already added");
      return;
    }
    setSelectedProducts((prev) => [
      ...prev,
      { productId: product.id, productName: product.name, quantity: 0 },
    ]);
    setShowDropdown(false);
    setSearchTerm("");
  };

  const handleAddCustomProduct = () => {
    if (!searchTerm) return;
    const exists = selectedProducts.find((p) => p.productName === searchTerm);
    if (exists) {
      toast.error("Product already added");
      return;
    }
    setSelectedProducts((prev) => [
      ...prev,
      { productId: null, productName: searchTerm, quantity: 0 },
    ]);
    setShowDropdown(false);
    setSearchTerm("");
  };

  const handleQuantityChange = (index: number, val: string) => {
    const newProducts = [...selectedProducts];
    newProducts[index].quantity = val === "" ? "" : Number(val);
    setSelectedProducts(newProducts);
  };

  const handleRemoveProduct = (index: number) => {
    setSelectedProducts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (selectedProducts.length === 0) {
      toast.error("Please add at least one product");
      return;
    }

    try {
      setSubmitting(true);
      const date = new Date();
      const payload = {
        clinicId: user?.id,
        products: selectedProducts.map((p) => ({
          productName: p.productName,
          quantity: Number(p.quantity) || 0,
        })),
        requestDate: date.toISOString().split("T")[0],
        requestStatus: "Pending",
      };

      const res = await InverntroyService.createStockRequest(payload);
      if (res.success) {
        toast.success("Request created successfully");
        onSuccess();
        onClose();
        setSelectedProducts([]);
      } else {
        toast.error(res.message || "Failed to create request");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error creating request");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">Create Product Request</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto">
          {/* Search Section */}
          <div className="relative mb-6">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search products..."
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowDropdown(true);
                  }}
                  onFocus={() => {
                    if (searchTerm) setShowDropdown(true);
                  }}
                />
              </div>
              <button
                onClick={handleAddCustomProduct}
                disabled={!searchTerm}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 whitespace-nowrap"
              >
                Add Custom
              </button>
            </div>

            {/* Dropdown Results */}
            {showDropdown && searchTerm.length > 0 && (
              <div className="absolute top-full left-0 right-0 bg-white border rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto z-10">
                {loading ? (
                  <div className="p-4 text-center text-gray-500">
                    Loading...
                  </div>
                ) : searchResults.length > 0 ? (
                  searchResults.map((product) => (
                    <div
                      key={product.id}
                      onClick={() => handleAddProduct(product)}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-0"
                    >
                      <div className="font-medium">{product.name}</div>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center text-gray-500">
                    No products found. You can add as custom product.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Selected Products Table */}
          {selectedProducts.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-3 font-medium text-gray-600 w-16">
                      S.No.
                    </th>
                    <th className="text-left p-3 font-medium text-gray-600">
                      Product Name
                    </th>
                    <th className="text-left p-3 font-medium text-gray-600 w-32">
                      Quantity
                    </th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {selectedProducts.map((item, index) => (
                    <tr key={index}>
                      <td className="p-3 text-sm text-gray-500 font-medium">{index + 1}</td>
                      <td className="p-3">
                        <span
                          className={!item.productId ? "text-blue-600" : ""}
                        >
                          {item.productName}
                        </span>
                      </td>
                      <td className="p-3">
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) =>
                            handleQuantityChange(index, e.target.value)
                          }
                          className="w-full border rounded px-2 py-1"
                          min="0"
                        />
                      </td>
                      <td className="p-3 text-center">
                        <button
                          onClick={() => handleRemoveProduct(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="p-4 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || selectedProducts.length === 0}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {submitting ? "Submitting..." : "Submit Request"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateRequestModal;
